import { useState, useEffect, useMemo, useCallback, type FormEvent } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2, LayoutList, FileEdit, Sparkles, ClipboardList } from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { applicationService } from '@core/supabase/services/applicationService';
import { activityService } from '@core/supabase/services/activityService';
import { candidateService } from '@core/supabase/services/candidateService';
import { clientService } from '@core/supabase/services/clientService';
import { jobService } from '@core/supabase/services/jobService';
import { orgService } from '@core/supabase/services/orgService';
import { Candidate, Client, Job, OrgUser } from '@core/types';
import { PageHeader } from '@shared/components/ui/PageHeader';
import { ContextAiAssistModal, AiAssistIconButton } from '@shared/components/ai/ContextAiAssistModal';
import { ApplicationNewOverview } from './ApplicationNewOverview';
import { ApplicationNewAiPanel } from './ApplicationNewAiPanel';
import { ApplicationNewTemplatesPanel } from './ApplicationNewTemplatesPanel';
import type { ApplicationNewAiContext } from './applicationAssistTypes';

type AppTab = 'overview' | 'details' | 'ai' | 'templates';

const TABS: { id: AppTab; label: string; icon: typeof LayoutList }[] = [
  { id: 'overview', label: '[Overview]', icon: LayoutList },
  { id: 'details', label: '[Details]', icon: FileEdit },
  { id: 'ai', label: '[AI assistant]', icon: Sparkles },
  { id: 'templates', label: '[Templates]', icon: ClipboardList },
];

export function ApplicationNewPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<AppTab>('details');

  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [teamMembers, setTeamMembers] = useState<OrgUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notesAiOpen, setNotesAiOpen] = useState(false);

  const [form, setForm] = useState({
    candidate_id: searchParams.get('candidateId') ?? '',
    client_id: searchParams.get('clientId') ?? '',
    job_id: searchParams.get('jobId') ?? '',
    assigned_to: '',
    internal_notes: '',
  });

  useEffect(() => {
    const cid = searchParams.get('clientId') ?? '';
    const jid = searchParams.get('jobId') ?? '';
    const candId = searchParams.get('candidateId') ?? '';
    if (cid || jid || candId) {
      setForm((f) => ({
        ...f,
        client_id: cid || f.client_id,
        job_id: jid || f.job_id,
        candidate_id: candId || f.candidate_id,
      }));
    }
  }, [searchParams]);

  useEffect(() => {
    if (!user?.orgId) return;
    Promise.all([
      candidateService.list(user.orgId),
      clientService.list(user.orgId),
      jobService.list(user.orgId, { status: 'open' }),
      orgService.getTeamMembers(user.orgId),
    ]).then(([c, cl, j, tm]) => {
      setCandidates(c);
      setClients(cl);
      setJobs(j);
      setTeamMembers(tm);
    });
  }, [user?.orgId]);

  useEffect(() => {
    const jid = searchParams.get('jobId');
    if (jid && jobs.length > 0) {
      const job = jobs.find((j) => j.id === jid);
      if (job?.client_id) {
        setForm((f) => (f.client_id ? f : { ...f, client_id: job.client_id }));
      }
    }
  }, [searchParams, jobs]);

  const handleJobChange = (jobId: string) => {
    const job = jobs.find((j) => j.id === jobId);
    setForm((f) => ({
      ...f,
      job_id: jobId,
      client_id: job?.client_id ?? f.client_id,
    }));
  };

  const set = (field: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [field]: e.target.value }));

  const applyNotes = useCallback((text: string, mode: 'append' | 'replace') => {
    setForm((f) => ({
      ...f,
      internal_notes:
        mode === 'replace'
          ? text
          : f.internal_notes.trim()
            ? `${f.internal_notes.trim()}\n\n${text}`
            : text,
    }));
    setTab('details');
  }, []);

  const aiContext: ApplicationNewAiContext = useMemo(() => {
    const cand = candidates.find((c) => c.id === form.candidate_id);
    const job = jobs.find((j) => j.id === form.job_id);
    const cl = clients.find((c) => c.id === form.client_id);
    return {
      candidateLabel: cand ? `${cand.full_name} — ${cand.email}` : '',
      jobTitle: job?.title ?? '',
      clientName: cl?.name ?? '',
      internal_notes: form.internal_notes,
    };
  }, [candidates, clients, jobs, form.candidate_id, form.job_id, form.client_id, form.internal_notes]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.candidate_id) {
      setError('Select a candidate');
      return;
    }
    if (!form.job_id) {
      setError('Select a job');
      return;
    }
    if (!form.client_id) {
      setError('Select a client');
      return;
    }
    if (!user?.orgId) return;

    setLoading(true);
    setError('');
    try {
      const app = await applicationService.create({
        org_id: user.orgId,
        candidate_id: form.candidate_id,
        job_id: form.job_id,
        client_id: form.client_id,
        assigned_to: form.assigned_to || null,
        internal_notes: form.internal_notes || null,
      });
      activityService.log(user.orgId, 'application', 'created', {
        entityId: app.id,
        actorId: user.orgUserId ?? undefined,
        payload: { application_id: app.id, candidate_id: form.candidate_id, job_id: form.job_id },
      });
      navigate(`/applications/${app.id}`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to create application';
      setError(msg.includes('unique') ? 'This candidate is already submitted for this job.' : msg);
    } finally {
      setLoading(false);
    }
  };

  const filteredJobs = form.client_id ? jobs.filter((j) => j.client_id === form.client_id) : jobs;

  if (!user?.orgId) return null;

  return (
    <div className="w-full max-w-4xl">
      <PageHeader
        icon={ClipboardList}
        breadcrumb={[
          { label: 'Pipeline', href: '/applications' },
          { label: 'Applications', href: '/applications' },
          { label: 'New application' },
        ]}
        title="New Application"
        subtitle="Submit a candidate for a role — use templates or AI for internal notes, then complete Details."
        action={
          <Link to="/applications" className="btn-secondary">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
        }
      />

      <div className="flex flex-wrap gap-1 border-b vscode-border mb-6">
        {TABS.map(({ id: tid, label, icon: Icon }) => (
          <button
            key={tid}
            type="button"
            onClick={() => setTab(tid)}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
              tab === tid
                ? 'border-brand-500 vscode-text-primary bg-[color-mix(in_srgb,var(--vscode-bg-tertiary)_50%,transparent)]'
                : 'border-transparent vscode-text-tertiary hover:vscode-text-secondary'
            }`}
          >
            <Icon className="h-4 w-4 opacity-80" />
            {label}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="card p-6">
          <ApplicationNewOverview />
        </div>
      )}

      {tab === 'details' && (
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="card space-y-4">
            <div>
              <label className="label">Candidate *</label>
              <select className="input" value={form.candidate_id} onChange={set('candidate_id')}>
                <option value="">Select candidate</option>
                {candidates.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.full_name} — {c.email}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">
                Client <span className="vscode-text-tertiary">(filter jobs by client)</span>
              </label>
              <select className="input" value={form.client_id} onChange={set('client_id')}>
                <option value="">All clients</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Job / Role *</label>
              <select className="input" value={form.job_id} onChange={(e) => handleJobChange(e.target.value)}>
                <option value="">Select open role</option>
                {filteredJobs.map((j) => (
                  <option key={j.id} value={j.id}>
                    {j.title}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="card space-y-4">
            <div>
              <label className="label">
                Assign to team member <span className="vscode-text-tertiary">(optional)</span>
              </label>
              <select className="input" value={form.assigned_to} onChange={set('assigned_to')}>
                <option value="">Unassigned</option>
                {teamMembers.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.display_name ?? m.email} ({m.role})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <label className="label mb-0">Internal notes</label>
                <AiAssistIconButton onClick={() => setNotesAiOpen(true)} title="AI assist — internal notes (split view)" />
              </div>
              <textarea
                className="input resize-none min-h-[120px]"
                rows={5}
                placeholder="Why this candidate for this role? Any caveats..."
                value={form.internal_notes}
                onChange={set('internal_notes')}
              />
            </div>
          </div>

          {error && (
            <p className="text-sm text-red-400 bg-red-900/20 border border-red-800 rounded-lg px-3 py-2">{error}</p>
          )}

          <div className="flex gap-2">
            <Link to="/applications" className="btn-secondary flex-1">
              Cancel
            </Link>
            <button type="submit" className="btn-primary flex-1" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin inline" /> Submitting...
                </>
              ) : (
                'Submit Application'
              )}
            </button>
          </div>
        </form>
      )}

      {tab === 'ai' && (
        <div className="card p-6">
          <ApplicationNewAiPanel context={aiContext} onApplyNotes={applyNotes} />
        </div>
      )}

      {tab === 'templates' && (
        <div className="card p-6">
          <ApplicationNewTemplatesPanel onApply={applyNotes} />
        </div>
      )}

      <ContextAiAssistModal
        open={notesAiOpen}
        onClose={() => setNotesAiOpen(false)}
        orgId={user.orgId}
        feature="ai_assist_application_new_internal_notes"
        preset="application_internal_notes"
        title="[AI — internal notes]"
        contextHint={`Candidate: ${aiContext.candidateLabel || '—'}\nJob: ${aiContext.jobTitle || '—'}\nClient: ${aiContext.clientName || '—'}`}
        original={form.internal_notes}
        onApply={(text, mode) =>
          setForm((f) => ({
            ...f,
            internal_notes:
              mode === 'replace'
                ? text
                : f.internal_notes.trim()
                  ? `${f.internal_notes.trim()}\n\n${text}`
                  : text,
          }))
        }
      />
    </div>
  );
}
